import { useEffect, useRef, useCallback } from 'react'
import { unlockAudioPlayback } from '../utils/audioUnlock'
import { Device, Call } from '@twilio/voice-sdk'
import { useAuthStore } from '../stores/authStore'
import { useCallStore } from '../stores/callStore'
import { useApi } from './useApi'
import { logger } from '../utils/logger'
import type { ApiError, HeldCall } from '../types'

// グローバル初期化状態（Singleton管理用）
let globalDeviceInitializing = false
let globalDeviceUserId: string | null = null
let globalTokenRefreshTimer: ReturnType<typeof setTimeout> | null = null

/** ログアウト時にグローバル状態をリセット（再ログイン時に新しいDeviceを初期化できるようにする） */
export function resetTwilioDeviceGlobals(): void {
  globalDeviceInitializing = false
  globalDeviceUserId = null
  if (globalTokenRefreshTimer) {
    clearTimeout(globalTokenRefreshTimer)
    globalTokenRefreshTimer = null
  }
}


interface OutboundCallMetadata {
  customerId?: string
  customerName?: string
  callerId?: string
}

interface HoldResponse {
  success: boolean
  holdQueueName: string
}

interface ResumeHoldResponse {
  holdQueueName: string
  callerNumber: string
  callHistoryId: string
  originalCallId: string
  callId: string
  message: string
}

interface AnswerResponse {
  success: boolean
  error?: string
  callId?: string
  callHistoryId?: string
}

interface InternalCallCreateResponse {
  success: boolean
  internalCallId: string
  error?: string
}

interface HeldCallsResponse {
  success: boolean
  heldCalls: HeldCall[]
}

interface PrepareOutboundResponse {
  callHistoryId: string
  customerName?: string
  customerId?: string
}

/**
 * Twilio Voice Device（ブラウザ内通話）を管理するカスタムフック
 * グローバル通話状態ストア（callStore）と統合
 * Singletonパターン: Deviceインスタンスはcallstore.twilioDeviceに保持され、
 * どのコンポーネントからでもアクセス可能だが初期化は1回のみ
 */
export function useTwilioDevice(onCallEnd?: (() => void) | null) {
  const { user } = useAuthStore()
  const api = useApi()

  // グローバル通話ストアを使用
  const {
    twilioDevice, // callStoreから既存のDeviceインスタンスを取得
    setDeviceStatus,
    setTwilioDevice,
    setCallStatus,
    startCall,
    startOutgoingCall,
    endCall,
    addCallToHistory,
    updateCallHistoryStatus,
  } = useCallStore()

  const onCallEndRef = useRef(onCallEnd)
  const activeCallSidRef = useRef<string | null>(null) // 現在処理中のCallSIDを追跡
  const setupCallSidsRef = useRef(new Set<string>()) // リスナー設定済みのCallSIDを追跡
  const setupCallListenersRef = useRef<((call: Call, callId?: string | null) => void) | null>(null) // setupCallListeners関数の最新参照を保持

  // onCallEndの最新値を保持
  useEffect(() => {
    onCallEndRef.current = onCallEnd
  }, [onCallEnd])

  /**
   * Twilio Access Tokenを取得
   */
  const fetchToken = useCallback(async (): Promise<string> => {
    try {
      const data = await api.get<{ token: string }>('/api/voice/token')
      return data.token
    } catch (err) {
      logger.error('Voiceトークン取得エラー:', err)
      throw new Error('Twilioトークンの取得に失敗しました: ' + (err as Error).message)
    }
  }, [api])

  /**
   * トークンを更新
   */
  const updateToken = useCallback(async () => {
    try {
      // ログアウト済みの場合は何もしない
      if (!useAuthStore.getState().isAuthenticated) return

      const token = await fetchToken()
      const device = useCallStore.getState().twilioDevice
      if (device) {
        await device.updateToken(token)
        logger.info('Twilioトークンを更新しました')
      }
    } catch (err) {
      // ログアウト後にタイマーが発火した場合はエラー状態にしない
      if (!useAuthStore.getState().isAuthenticated) return

      logger.error('トークン更新エラー:', err)
      setDeviceStatus('error', 'トークンの更新に失敗しました')
    }
  }, [fetchToken, setDeviceStatus])

  /**
   * Callイベントリスナーのセットアップ
   */
  const setupCallListeners = useCallback((call: Call, callId: string | null = null) => {
    const callSid = call.parameters?.CallSid
    const from = call.parameters?.From
    const to = call.parameters?.To
    // callTypeパラメータで発信/着信を判定（バックエンドから送信される）
    const callType = call.parameters?.callType
    const direction = callType === 'outbound' ? 'outbound' : 'inbound'

    // 既にリスナーが設定されている場合はスキップ
    if (callSid && setupCallSidsRef.current.has(callSid)) {
      logger.debug('Call listenersは既にセットアップ済みのためスキップ:', callSid)
      return
    }

    // CallSidを記録
    if (callSid) {
      setupCallSidsRef.current.add(callSid)
    }

    // [DIAG] Call._onHangup をラップしてペイロードをログ出力
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const callAny = call as any
    if (typeof callAny._onHangup === 'function') {
      const originalOnHangup = callAny._onHangup.bind(callAny)
      callAny._onHangup = (payload: Record<string, unknown>) => {
        logger.warn('[DIAG _onHangup] ペイロード:', {
          callSid,
          payload: JSON.stringify(payload),
          timestamp: Date.now(),
        })
        originalOnHangup(payload)
      }
      logger.info('[DIAG] _onHangup ラップ成功:', callSid)
    } else {
      logger.warn('[DIAG] _onHangup が見つかりません:', callSid)
    }

    // internalCallIdを取得（着信側はcustomParameters、発信側はparameters）
    let internalCallId: string | null = null
    if (call.customParameters?.has("internalCallId")) {
      internalCallId = call.customParameters.get("internalCallId") || null
    } else if (call.parameters?.internalCallId) {
      // 発信側: twilioDevice.connect({ params: { internalCallId } }) で渡した場合
      internalCallId = call.parameters.internalCallId
    }
    // 内線通話の判定: internalCallIdが存在する場合のみ内線通話
    // キューからの転送着信はtoが'client:'で始まるが、internalCallIdがないため内線通話ではない
    const isInternalCall = !!internalCallId

    logger.debug('Call listenersセットアップ:', {
      callSid,
      callId,
      direction,
      from,
      to,
      isInternalCall,
    })

    call.on('accept', () => {
      // 診断ログ: accept時のDevice通話リスト
      const device = useCallStore.getState().twilioDevice
      const deviceCalls = device?.calls ? device.calls.map((c: Call) => ({
        sid: c.parameters?.CallSid,
        status: c.status?.(),
      })) : []
      logger.warn('[DIAG accept] accept fired:', {
        callSid, callId, isInternalCall,
        deviceCallsCount: device?.calls?.length ?? 0,
        deviceCalls,
        storeCallStatus: useCallStore.getState().callStatus,
        storeActiveCallSid: useCallStore.getState().activeCall?.parameters?.CallSid,
        timestamp: Date.now(),
      })

      // endCall() が call.accept() と call.on('accept') の間で呼ばれた場合、
      // disconnectAll() は接続中の通話を切断できないため、ここで明示的に切断する。
      // 「disconnectAllが呼ばれているのに通話が続く」問題の根本対策。
      const preAcceptState = useCallStore.getState()
      if (preAcceptState.callStatus === 'idle' && !preAcceptState.currentCallId) {
        logger.warn('[CallEvent] accept fired but state already idle — disconnecting escaped call:', {
          callSid, callId, timestamp: Date.now(),
        })
        call.disconnect()
        return
      }

      startCall(call, callId || internalCallId)

      // 内線通話の場合はinternalCallStatusも更新し、着信表示をクリア
      if (isInternalCall) {
        const { setInternalCallStatus, clearIncomingInternalCall, incomingInternalCall } = useCallStore.getState()
        setInternalCallStatus('connected')
        // 内線通話IDを保存（転送機能で使用）
        useCallStore.setState({ currentInternalCallId: internalCallId })

        // 着信側: internalCallPeerUserId を設定（発信者のユーザーID）
        // incomingInternalCall.callerIdから取得（WebSocketで送信されている）
        if (incomingInternalCall?.callerId) {
          useCallStore.setState({ internalCallPeerUserId: incomingInternalCall.callerId })
          logger.debug('着信側: internalCallPeerUserId設定:', incomingInternalCall.callerId)
        }

        clearIncomingInternalCall()
      }

      // 通話履歴に追加（オペレーターのキュー接続・内線通話は履歴に残さない）
      if (!call.parameters?.queueName && !isInternalCall) {
        addCallToHistory({
          callId: callId || callSid,
          from,
          to,
          status: 'in-progress',
          date: Date.now(),
          twilioCallSid: callSid,
          direction,
        })
      }
    })

    call.on('disconnect', async () => {
      // 診断ログ: disconnect時のDevice通話リスト
      const device = useCallStore.getState().twilioDevice
      const deviceCalls = device?.calls ? device.calls.map((c: Call) => ({
        sid: c.parameters?.CallSid,
        status: c.status?.(),
      })) : []
      logger.warn('[DIAG disconnect] disconnect fired:', {
        callSid, callId, isInternalCall,
        deviceCallsCount: device?.calls?.length ?? 0,
        deviceCalls,
        storeCallStatus: useCallStore.getState().callStatus,
        storeActiveCallSid: useCallStore.getState().activeCall?.parameters?.CallSid,
        storeCurrentCallId: useCallStore.getState().currentCallId,
        timestamp: Date.now(),
      })

      const currentStore = useCallStore.getState()

      // Already-idle guard:
      // 転送着信時にincomingハンドラが内線通話の状態を先にクリアしてからdisconnect()する。
      // この場合、状態は既にidleなのでendCall()は不要。
      // endCall()はdisconnectAll()を呼ぶため、新しい着信（未accept）を巻き込む可能性がある。
      if (currentStore.callStatus === 'idle' && currentStore.activeCall == null) {
        logger.debug('[call.on(disconnect)] State already idle — skipping endCall:', { callSid, callId })

        // 内線通話のバックエンド通知だけは行う
        if (isInternalCall && internalCallId) {
          try {
            await api.post(`/api/internal-calls/${internalCallId}/end`)
          } catch (err) {
            if ((err as ApiError).status !== 409) logger.error('内線通話終了通知エラー:', err)
          }
        }

        activeCallSidRef.current = null
        if (onCallEndRef.current) onCallEndRef.current()
        return
      }

      // Stale disconnect guard:
      // 保留→復帰の際、古い通話のdisconnectイベントが新しい通話の接続後に
      // 発火するレースコンディションを防止。
      // endCall()はtwilioDevice.disconnectAll()を呼ぶため、新しい通話まで切断してしまう。
      const activeCallMismatch = currentStore.activeCall != null && currentStore.activeCall !== call
      const connectingCallMismatch = callId != null
        && currentStore.currentCallId != null
        && currentStore.currentCallId !== callId

      if (activeCallMismatch || connectingCallMismatch) {
        logger.warn('[call.on(disconnect)] Stale disconnect event — skipping endCall:', {
          disconnectedCallId: callId,
          disconnectedCallSid: callSid,
          storeCurrentCallId: currentStore.currentCallId,
          activeCallMismatch,
          connectingCallMismatch,
        })

        // 内線通話のバックエンド通知だけは行う
        if (isInternalCall && internalCallId) {
          try {
            await api.post(`/api/internal-calls/${internalCallId}/end`)
          } catch (err) {
            if ((err as ApiError).status !== 409) logger.error('内線通話終了通知エラー:', err)
          }
        }

        activeCallSidRef.current = null
        return
      }

      // 内線通話の場合は状態をクリア＆バックエンドに通知して終了
      // endCall()にfall-throughしない（disconnectAll()が転送着信を巻き込むのを防止）
      if (isInternalCall && internalCallId) {
        // callStatus/activeCallも含めて全状態を同期的にクリアする。
        // これにより、await中に device.on('incoming') が発火した場合、
        // incoming ハンドラーがidle状態を検出して転送着信を正常に受け入れる。
        useCallStore.setState({
          activeCall: null,
          currentCallId: null,
          currentInternalCallId: null,
          internalCallPeerUserId: null,
          incomingInternalCall: null,
          internalCallStatus: 'idle',
          callStatus: 'idle',
          callerNumber: null,
        })

        activeCallSidRef.current = null

        // バックエンドに通話終了を通知（WebSocketメッセージを送信させる）
        try {
          await api.post(`/api/internal-calls/${internalCallId}/end`)
          logger.info('内線通話終了をバックエンドに通知しました')
        } catch (err) {
          // 409エラー（既に終了済み）は正常なケースとして無視
          if ((err as ApiError).status === 409) {
            logger.debug('内線通話は既に終了しています（正常）')
          } else {
            logger.error('内線通話終了通知エラー:', err)
          }
        }

        if (onCallEndRef.current) onCallEndRef.current()
        return
      }

      // 通話履歴のステータスを更新
      if (callSid) {
        updateCallHistoryStatus(callSid, 'completed')
      }

      activeCallSidRef.current = null

      // Re-check state before endCall:
      // 非同期処理（api.post等）の間に device.on('incoming') が発火して
      // 新しい着信（転送着信など）が設定されている可能性がある。
      // endCall() は disconnectAll() + incomingCall: null を行うため、
      // 新しい着信を破壊してしまう。
      const preEndState = useCallStore.getState()
      if (preEndState.incomingCall || (preEndState.activeCall && preEndState.activeCall !== call)) {
        logger.debug('[call.on(disconnect)] New call detected after async — skipping endCall:', {
          hasIncomingCall: !!preEndState.incomingCall,
          hasNewActiveCall: preEndState.activeCall && preEndState.activeCall !== call,
        })
        if (onCallEndRef.current) onCallEndRef.current()
        return
      }

      // Device上にこの通話以外のアクティブな通話があるかチェック
      // 2つの着信が来た場合、最初の通話のdisconnectで2番目の通話の状態をリセットしないようにする
      const deviceForCheck = useCallStore.getState().twilioDevice
      if (deviceForCheck?.calls) {
        const otherActiveCalls = deviceForCheck.calls.filter((c: Call) => c !== call)
        if (otherActiveCalls.length > 0) {
          logger.warn('[CallEvent] disconnect but device has other active calls — skipping state reset:', {
            callSid, callId,
            otherCalls: otherActiveCalls.map((c: Call) => ({
              sid: c.parameters?.CallSid,
              status: c.status?.(),
            })),
            timestamp: Date.now(),
          })
          // この通話のdisconnectでは状態リセットしない。
          // 他のアクティブな通話のacceptイベントでstartCallが呼ばれて状態が正しく設定される。
          if (onCallEndRef.current) onCallEndRef.current()
          return
        }
      }

      // disconnect イベントが発火した時点で通話は既に切断されている。
      // endCall() は disconnectAll() を呼ぶため、接続途中の別の通話まで巻き添えにする。
      // ここでは disconnectAll() なしの state リセットのみ行う。
      logger.warn('[CallEvent] disconnect → resetting state (no disconnectAll):', { callSid, callId, timestamp: Date.now() })
      useCallStore.setState({
        activeCall: null,
        incomingCall: null,
        currentCallId: null,
        currentCallSid: null,
        currentCallHistoryId: null,
        callerNumber: null,
        customerName: null,
        phoneNumberName: null,
        callStatus: 'idle',
        callStartTime: null,
        callDuration: 0,
        isMuted: false,
        isSpeakerOn: true,
        incomingInternalCall: null,
        internalCallStatus: 'idle',
        ignoredCallIds: new Set<string>(),
      })
      if (onCallEndRef.current) onCallEndRef.current()
    })

    call.on('reject', () => {
      logger.info('[CallEvent] reject fired:', { callSid, callId, timestamp: Date.now() })

      // 通話履歴に追加（拒否）
      addCallToHistory({
        callId: callId || callSid,
        from,
        to,
        status: 'canceled',
        date: Date.now(),
        duration: 0,
        twilioCallSid: callSid,
        direction, // Twilioの Direction パラメータ（参考情報）
      })

      activeCallSidRef.current = null
      setCallStatus('idle')
      if (onCallEndRef.current) onCallEndRef.current()
    })

    call.on('cancel', () => {
      logger.info('[CallEvent] cancel fired:', { callSid, callId, timestamp: Date.now() })

      const currentStore = useCallStore.getState()

      // Stale cancel guard: cancel should only affect ringing/idle state.
      // If we're already connecting or connected (e.g., auto-accepted queue transfer),
      // this cancel event is stale and should be ignored.
      if (currentStore.callStatus === 'connecting' || currentStore.callStatus === 'connected') {
        logger.warn('[CallEvent] cancel during connecting/connected — ignoring:', {
          callSid, callId, callStatus: currentStore.callStatus,
        })
        return
      }

      // Stale cancel guard: if activeCall exists and it's a different call, ignore
      if (currentStore.activeCall != null && currentStore.activeCall !== call) {
        logger.warn('[CallEvent] cancel for stale call — ignoring:', { callSid, callId })
        return
      }

      // 通話履歴のステータスを更新
      if (callSid) {
        updateCallHistoryStatus(callSid, 'canceled')
      }

      activeCallSidRef.current = null
      setCallStatus('idle')
      if (onCallEndRef.current) onCallEndRef.current()
    })

    call.on('error', (error: { message: string }) => {
      logger.error('[CallEvent] error fired:', { error, callSid, callId, timestamp: Date.now() })

      const currentStore = useCallStore.getState()

      // Stale error guard: if activeCall exists and it's a different call, ignore
      if (currentStore.activeCall != null && currentStore.activeCall !== call) {
        logger.warn('[CallEvent] error for stale call — ignoring:', { callSid, callId })
        return
      }

      // 通話履歴に追加（エラー）
      addCallToHistory({
        callId: callId || callSid,
        from,
        to,
        status: 'failed',
        date: Date.now(),
        duration: 0,
        twilioCallSid: callSid,
        direction,
      })

      setDeviceStatus('error', error.message)
      setCallStatus('idle')
      if (onCallEndRef.current) onCallEndRef.current()
    })

    call.on('mute', (isMuted: boolean) => {
      logger.debug('ミュート状態:', isMuted)
    })
  }, [startCall, endCall, setCallStatus, setDeviceStatus, addCallToHistory, updateCallHistoryStatus])

  // setupCallListenersの最新参照を保持（ローカルref + callStore両方に登録）
  useEffect(() => {
    setupCallListenersRef.current = setupCallListeners
    useCallStore.getState().setSetupCallListeners(setupCallListeners)
  }, [setupCallListeners])

  /**
   * Deviceイベントリスナーのセットアップ
   */
  const setupDeviceListeners = useCallback((dev: Device) => {
    // 多重登録を防ぐために既存のリスナーをクリア
    dev.removeAllListeners()

    // 登録完了
    dev.on('registered', () => {
      logger.info('Device registered')
      setDeviceStatus('ready')
    })

    // 登録解除
    dev.on('unregistered', () => {
      logger.info('Device unregistered')
      setDeviceStatus('offline')
    })

    // エラー
    dev.on('error', (error: { message: string }) => {
      logger.error('Device error:', error)
      setDeviceStatus('error', error.message)
    })

    // 着信イベント（内線通話で使用）
    dev.on('incoming', async (call: Call) => {
      const callSid = call.parameters.CallSid
      const from = call.parameters.From
      const to = call.parameters.To
      let internalCallId: string | undefined
      if (call.customParameters) {
        internalCallId = call.customParameters.get('internalCallId')
      }
      let displayName: string | undefined
      if (call.customParameters) {
        displayName = call.customParameters.get('displayName')
      }
      const callerName = call.parameters.callerName || call.customParameters?.get?.('callerName')

      // 診断ログ: Device上の全通話を表示
      const deviceCalls = dev.calls ? dev.calls.map((c: Call) => ({
        sid: c.parameters?.CallSid,
        status: c.status?.(),
      })) : []
      const storeState = useCallStore.getState()
      logger.warn('[DIAG incoming] 着信イベント発火:', {
        callSid,
        from,
        to,
        internalCallId,
        deviceCallsCount: dev.calls?.length ?? 0,
        deviceCalls,
        storeCallStatus: storeState.callStatus,
        storeActiveCallSid: storeState.activeCall?.parameters?.CallSid,
        storeCurrentCallId: storeState.currentCallId,
        timestamp: Date.now(),
      })

      logger.debug('内線着信:', {
        CallSid: callSid,
        From: from,
        To: to,
        internalCallId,
        callerName,
        allParameters: call.parameters,
        customParameters: call.customParameters,
      })

      // internalCallIdの有無で処理を分岐
      if (!internalCallId) {
        // internalCallIdが無い = テスト着信 or 予期しない着信（<Dial><Client>からの着信）
        // ※キュー応答・保留復帰・転送はすべて device.connect() + Queue 方式のため incoming は発火しない
        // ※このブランチは安全策として残す（テスト着信等の対応）

        // customParametersからcallId/callerNumber/callHistoryIdを取得
        const transferCallId = call.customParameters?.get?.('callId')
        const transferCallerNumber = call.customParameters?.get?.('callerNumber')
        const transferCallHistoryId = call.customParameters?.get?.('callHistoryId')

        const effectiveCallId = transferCallId || callSid

        logger.info('[転送/テスト着信] 着信を自動受け入れ:', {
          transferCallId,
          transferCallerNumber,
          transferCallHistoryId,
          effectiveCallId,
        })

        // callerNumberを状態に設定（転送着信時の発信元表示用）
        if (transferCallerNumber) {
          useCallStore.getState().setCallerNumber(transferCallerNumber)
        }

        // callHistoryIdを状態に設定（メモ保存用）
        if (transferCallHistoryId) {
          useCallStore.getState().setCurrentCallHistoryId(transferCallHistoryId)
        }

        // currentCallIdを設定（保留時にcallIdとして使用される）
        useCallStore.getState().setCurrentCallId(effectiveCallId)

        // 通話イベントリスナーを設定
        if (setupCallListenersRef.current) {
          setupCallListenersRef.current(call, effectiveCallId)
        }

        useCallStore.getState().setCallStatus('connecting')

        // 自動応答
        call.accept()
        logger.info('[転送/テスト着信] 自動応答:', {
          callId: effectiveCallId,
          callSid,
          timestamp: Date.now(),
        })

        return
      }

      // internalCallIdがある = 内線通話
      // 通話中の場合は着信を自動的に拒否
      const { callStatus, isInCall } = useCallStore.getState()
      if (isInCall() || ['connecting', 'connected'].includes(callStatus)) {
        logger.debug('通話中のため内線着信を拒否します')
        call.reject()
        return
      }

      // callStoreに内線着信を設定
      const { setIncomingInternalCall } = useCallStore.getState()
      const internalCallObj = {
        internalCallId,
        callerName: displayName || callerName || '内線',
        twilioCall: call, // Twilio Callオブジェクトを保存
      }

      logger.debug('setIncomingInternalCallに渡すオブジェクト:', internalCallObj)
      setIncomingInternalCall(internalCallObj)

      // 内線着信のCallオブジェクトにもイベントリスナーを設定（最新の関数を使用）
      if (setupCallListenersRef.current) {
        setupCallListenersRef.current(call, internalCallId)
      }

      // 内線着信時はcallStatusを変更しない（incomingInternalCallの存在で内線着信バーが表示される）
      // callStatusをringingにすると外線着信バーと競合するため

      // 設定後に確認
      const { incomingInternalCall } = useCallStore.getState()
      logger.debug('設定後のincomingInternalCall:', incomingInternalCall)
    })

    // NOTE: dev.on('connect') と dev.on('disconnect') は Twilio Voice SDK 2.x の公式APIではない。
    // SDK更新後に着信時にも発火するようになり、setupCallListeners の二重登録や
    // endCall() の誤呼び出しを引き起こすため削除。
    // 全ての通話パス（answerQueueCall, makeInternalCall, makeOutboundCall）で
    // setupCallListeners を正しく呼んでいるため、Device レベルのハンドラは不要。

    // トークン更新が必要
    dev.on('tokenWillExpire', () => {
      logger.info('トークンの有効期限が近づいています')
      updateToken()
    })
  }, [endCall, setCallStatus, setDeviceStatus, setupCallListeners, startOutgoingCall, updateToken])

  /**
   * Deviceを初期化
   */
  const initializeDevice = useCallback(async () => {
    try {
      setDeviceStatus('initializing')

      const token = await fetchToken()

      // Deviceインスタンスを作成
      const newDevice = new Device(token, {
        logLevel: 'warn', // 'debug' から 'warn' に変更してログを削減
        // 着信を有効化（内線通話中に転送着信を受け取るためtrue必須）
        allowIncomingWhileBusy: true,
        // エッジロケーション（東京に近いシンガポールを優先）
        edge: ['singapore', 'tokyo', 'ashburn'],
        // コーデック設定
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      })

      // イベントリスナー登録
      setupDeviceListeners(newDevice)

      // Deviceを登録
      await newDevice.register()

      // callStoreに保存（これがSingle Source of Truth）
      setTwilioDevice(newDevice)
      setDeviceStatus('ready')

      logger.info('Twilio Device登録成功')

      // トークン有効期限の55分後に再取得（1時間有効なので余裕を持って）
      if (globalTokenRefreshTimer) {
        clearTimeout(globalTokenRefreshTimer)
      }
      globalTokenRefreshTimer = setTimeout(() => {
        logger.info('Twilioトークンを更新します')
        updateToken()
      }, 55 * 60 * 1000)

    } catch (err) {
      logger.error('Device初期化エラー:', err)
      setDeviceStatus('error', (err as Error).message)
    }
  }, [fetchToken, setDeviceStatus, setTwilioDevice, setupDeviceListeners, updateToken])

  /**
   * Deviceを破棄
   */
  const destroyDevice = useCallback(() => {
    // callStoreからDeviceインスタンスを取得
    const device = useCallStore.getState().twilioDevice

    if (device) {
      device.destroy()
      setTwilioDevice(null)
      setDeviceStatus('offline')
      logger.info('Device destroyed')
    }

    if (globalTokenRefreshTimer) {
      clearTimeout(globalTokenRefreshTimer)
    }
  }, [setTwilioDevice, setDeviceStatus])

  // 初期化（Singletonパターン: callStore.twilioDeviceを使用）
  useEffect(() => {
    let mounted = true

    if (user) {
      const currentUserId = user.sub ?? null

      // callStoreから既存のDeviceを確認
      const existingDevice = useCallStore.getState().twilioDevice

      // 既に同じユーザーでDevice初期化済みまたは初期化中の場合はスキップ
      if (globalDeviceUserId === currentUserId && (existingDevice || globalDeviceInitializing)) {
        logger.debug('Device already initialized or initializing for user:', currentUserId)
        return
      }

      // ユーザーが変わった場合は既存のDeviceを破棄
      if (existingDevice && globalDeviceUserId !== currentUserId) {
        logger.info('User changed - destroying old device')
        existingDevice.destroy()
        globalDeviceUserId = null
        setTwilioDevice(null)
        setDeviceStatus('offline')
      }

      // 新しいDeviceを初期化
      if (!existingDevice && !globalDeviceInitializing && mounted) {
        globalDeviceInitializing = true
        globalDeviceUserId = currentUserId
        logger.info('Initializing new device for user:', currentUserId)

        initializeDevice().then(() => {
          globalDeviceInitializing = false
        }).catch((err) => {
          logger.error('Device initialization failed:', err)
          globalDeviceInitializing = false
          globalDeviceUserId = null
        })
      }
    }

    return () => {
      mounted = false
      // 注: グローバルDeviceはアプリ全体で共有されるため、ここでは破棄しない
      // 破棄はログアウト時やユーザー変更時のみ
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub]) // user.subが変わったら再初期化

  /**
   * 通話を保留する（callIdで指定）
   */
  const holdCall = useCallback(async (callId: string) => {
    try {
      if (!callId) {
        throw new Error('callIdがありません')
      }

      // callHistoryIdを取得（Phase 8: CallHistoryId Priority Architecture）
      const { currentCallHistoryId } = useCallStore.getState()

      logger.debug('[holdCall] 保留を実行:', { callId, callHistoryId: currentCallHistoryId })

      // バックエンドで発信者を保留キューに転送（callHistoryIdを含める）
      const response = await api.post<HoldResponse>(`/api/calls/${callId}/hold`, {
        callHistoryId: currentCallHistoryId
      })

      logger.debug('[holdCall] バックエンド応答:', response)

      if (response.success) {
        // 状態にキュー名を保存
        useCallStore.getState().setHoldQueueName(response.holdQueueName)

        // オペレーター側の通話を切断
        const activeCall = useCallStore.getState().activeCall
        if (activeCall) {
          logger.debug('[holdCall] オペレーター側の通話を切断')
          activeCall.disconnect()
        }

        // 通話状態をクリア
        useCallStore.getState().endCall()

        // 保留一覧を更新
        try {
          const heldCallsResponse = await api.get<HeldCallsResponse>('/api/calls/held')
          if (heldCallsResponse.success) {
            useCallStore.getState().setHeldCalls(heldCallsResponse.heldCalls)
          }
        } catch (fetchError) {
          logger.error('保留一覧取得エラー:', fetchError)
        }
      }

      return response
    } catch (error) {
      logger.error('保留エラー:', error)
      throw error
    }
  }, [api])

  /**
   * 保留を解除する（device.connect() + Queue方式）
   */
  const resumeHeldCall = useCallback(async (holdQueueName: string) => {
    const device = useCallStore.getState().twilioDevice
    if (!device) {
      throw new Error('Twilio Deviceが初期化されていません')
    }

    try {
      logger.debug('[resumeHeldCall] 保留解除リクエスト:', holdQueueName)

      // Step 1: バックエンドで排他制御 + 保留情報取得
      const response = await api.post<ResumeHoldResponse>('/api/calls/resume-hold', {
        holdQueueName
      })

      const { originalCallId, callerNumber, callHistoryId } = response

      logger.info('[resumeHeldCall] 排他制御成功:', {
        holdQueueName,
        originalCallId,
        callerNumber,
        callHistoryId,
      })

      // callHistoryIdを保存
      if (callHistoryId) {
        useCallStore.getState().setCurrentCallHistoryId(callHistoryId)
      }

      // 保留キュー名をクリア
      useCallStore.getState().clearHoldQueueName()

      // 保留一覧から削除
      useCallStore.getState().removeHeldCall(holdQueueName)

      // callStatusをconnectingに遷移
      setCallStatus('connecting')

      // Step 2: device.connect() で保留キューに接続
      // handleIncomingCall の hold- ブランチが <Dial><Queue> を返す
      const call = await device.connect({
        params: {
          queueName: holdQueueName,
          originalCallId,
          callerNumber,
        }
      })

      // 通話状態を設定（activeCall, currentCallId等）
      startOutgoingCall(call, originalCallId)

      // 通話イベントリスナーを設定
      if (setupCallListenersRef.current) {
        setupCallListenersRef.current(call, originalCallId)
      }

      logger.info(`[resumeHeldCall] device.connect() 発行完了: callId=${originalCallId}`)

      return {
        success: true,
        message: '保留を解除しました',
        holdQueueName
      }

    } catch (error) {
      logger.error('[resumeHeldCall] 保留解除エラー:', error)

      const apiError = error as ApiError

      // 409エラー（排他制御失敗）の場合
      if (apiError.response?.status === 409) {
        useCallStore.getState().removeHeldCall(holdQueueName)
        useCallStore.getState().clearHoldQueueName()
        throw new Error(apiError.response.data.error || '既に他のオペレータが保留を解除しています')
      }

      // 400エラー（通話が既に切断されている）の場合
      if (apiError.response?.status === 400) {
        logger.info('[resumeHeldCall] 通話が既に切断されています。保留状態をクリアします')
        useCallStore.getState().removeHeldCall(holdQueueName)
        useCallStore.getState().clearHoldQueueName()
        setCallStatus('idle')
        return {
          success: false,
          message: '通話は既に終了しています',
          holdQueueName
        }
      }

      // その他のエラーの場合は保留リストを再取得
      try {
        const heldCallsResponse = await api.get<HeldCallsResponse>('/api/calls/held')
        if (heldCallsResponse.success) {
          useCallStore.getState().setHeldCalls(heldCallsResponse.heldCalls)
        }
      } catch (fetchError) {
        logger.error('[resumeHeldCall] 保留一覧再取得エラー:', fetchError)
      }

      // エラー時: callStatusをidleに戻す
      useCallStore.getState().clearHoldQueueName()
      setCallStatus('idle')

      throw error
    }
  }, [api, setCallStatus, startOutgoingCall])

  /**
   * キュー通話を受話（device.connect + Queue方式）
   * 1. サーバー側で排他制御（QueuedCallsロック）
   * 2. device.connect({ queueName: callId }) でキューに接続
   * 3. handleIncomingCall queueNameブランチが <Dial><Queue> TwiMLを返す
   * 4. Queueが自動デキューして顧客と接続
   */
  const answerQueueCall = useCallback(async (callSid: string) => {
    // iOS Safari: タップの同期文脈で音声再生を解錠 (await 後では効かない)
    unlockAudioPlayback()
    if (!user) {
      throw new Error('ユーザー情報が取得できません')
    }
    const device = useCallStore.getState().twilioDevice
    if (!device) {
      throw new Error('Twilio Deviceが初期化されていません')
    }

    try {
      // 応答処理開始: callStatusをconnectingに遷移
      setCallStatus('connecting')

      logger.debug(`[AnswerQueue] 排他制御リクエスト送信: callSid=${callSid}`)

      // Step 1: 排他制御（サーバー側でQueuedCallsをロック + call.answered WS送信）
      const response = await api.post<AnswerResponse>(`/api/calls/${callSid}/answer`, {})
      if (!response.success) {
        throw new Error(response.error || '受話処理に失敗しました')
      }

      const { callId, callHistoryId } = response
      if (!callId) {
        throw new Error('サーバーからcallIdが返却されませんでした')
      }
      logger.info(`[AnswerQueue] 排他制御成功: callId=${callId}, callHistoryId=${callHistoryId}`)

      // callHistoryIdを保存（メモ保存等で使用）
      if (callHistoryId) {
        useCallStore.getState().setCurrentCallHistoryId(callHistoryId)
      }

      // Step 2: device.connect() でキューに接続
      // handleIncomingCall の queueName ブランチが <Dial><Queue> を返す
      const call = await device.connect({
        params: {
          queueName: callId,
        }
      })

      // 通話状態を設定（activeCall, callStatus='connecting', etc）
      startOutgoingCall(call, callId)

      // 通話イベントリスナーを設定（disconnect/error等のハンドリング）
      if (setupCallListenersRef.current) {
        setupCallListenersRef.current(call, callId)
      }

      logger.info(`[AnswerQueue] device.connect() 発行完了: callId=${callId}`)

    } catch (error) {
      logger.error('[AnswerQueue] 受話エラー:', error)
      // エラー時: キューに残りがあればringing、なければidle
      const { queuedCalls } = useCallStore.getState()
      setCallStatus(queuedCalls.length > 0 ? 'ringing' : 'idle')
      throw error
    }
  }, [api, user, setCallStatus, startOutgoingCall])

  /**
   * 保留中の通話一覧を取得
   */
  const fetchHeldCalls = useCallback(async () => {
    try {
      const response = await api.get<HeldCallsResponse>('/api/calls/held')

      if (response.success) {
        useCallStore.getState().setHeldCalls(response.heldCalls)
      }

      return response.heldCalls || []
    } catch (error) {
      logger.error('保留一覧取得エラー:', error)
      return []
    }
  }, [api])

  /**
   * アウトバウンドコール（顧客への発信）
   */
  const makeOutboundCall = useCallback(async (phoneNumber: string, metadata: OutboundCallMetadata = {}) => {
    // callStoreからDeviceインスタンスを取得
    const device = useCallStore.getState().twilioDevice

    if (!device) {
      throw new Error('Twilio Deviceが初期化されていません')
    }

    if (!phoneNumber) {
      throw new Error('電話番号が指定されていません')
    }

    // 通話中チェック
    const { isInCall, callStatus } = useCallStore.getState()
    if (isInCall() || ['connecting', 'connected'].includes(callStatus)) {
      throw new Error('通話中は発信できません')
    }

    console.log('アウトバウンドコール開始:', { phoneNumber, metadata })

    // CallHistoryId優先アーキテクチャ: バックエンドでCallHistoryを事前作成
    let callHistoryId: string | null = null
    try {
      const prepareResponse = await api.post<PrepareOutboundResponse>('/api/call-history/prepare-outbound', {
        phoneNumber,
        customerId: metadata.customerId,
        customerName: metadata.customerName,
        callerId: metadata.callerId,
      })

      callHistoryId = prepareResponse.callHistoryId
      const customerName = prepareResponse.customerName
      const customerId = prepareResponse.customerId

      console.log('CallHistory事前作成完了:', { callHistoryId, customerName, customerId })

      // callHistoryIdを保存（メモ保存時に使用）
      useCallStore.getState().setCurrentCallHistoryId(callHistoryId)
    } catch (prepareError) {
      console.error('CallHistory事前作成エラー（発信は継続）:', prepareError)
      // エラーでも発信は継続（フォールバック: 従来のフロー）
    }

    // Twilio Voice SDKで発信（callHistoryIdを含める）
    const call = await device.connect({
      params: {
        To: phoneNumber,
        callHistoryId: callHistoryId || '', // CallHistoryId優先アーキテクチャ
        customerId: metadata.customerId || '',
        customerName: metadata.customerName || '',
        callType: 'outbound', // 発信であることを明示
      }
    })

    // 通話状態を設定
    startOutgoingCall(call)
    // 発信時は発信先の電話番号をcallerNumberに設定（CustomerSidePanelで顧客検索に使用）
    useCallStore.getState().setCallerNumber(phoneNumber)

    // 通話イベントリスナーを設定
    if (setupCallListenersRef.current) {
      setupCallListenersRef.current(call, null)
    }

    return call
  }, [api, startOutgoingCall])

  /**
   * 内線通話を発信
   */
  const makeInternalCall = useCallback(async (targetUserId: string, targetName: string) => {
    unlockAudioPlayback()
    const device = useCallStore.getState().twilioDevice
    if (!device) {
      throw new Error('Twilio Deviceが初期化されていません')
    }

    const { isInCall, callStatus } = useCallStore.getState()
    if (isInCall() || ['connecting', 'connected'].includes(callStatus)) {
      throw new Error('通話中は内線発信できません')
    }

    // バックエンドに内線通話を作成
    const response = await api.post<InternalCallCreateResponse>('/api/internal-calls', { targetUserId })
    if (!response.success) {
      throw new Error(response.error || '内線通話の作成に失敗しました')
    }

    const { internalCallId } = response
    const targetIdentity = `operator-${targetUserId}`
    const targetClient = `client:${targetIdentity}`

    logger.debug('[makeInternalCall] 内線発信開始:', { targetClient, internalCallId })

    const call = await device.connect({
      params: {
        To: targetClient,
        internalCallId,
        callType: 'outbound',
      }
    })

    // 通話状態を設定
    startOutgoingCall(call, internalCallId)
    useCallStore.getState().setCallerNumber(targetName)
    useCallStore.getState().setInternalCallStatus('ringing')
    useCallStore.setState({
      currentInternalCallId: internalCallId,
      internalCallPeerUserId: targetUserId,
    })

    // 通話イベントリスナーを設定（disconnect/accept/error等）
    if (setupCallListenersRef.current) {
      setupCallListenersRef.current(call, internalCallId)
    }

    return { success: true, internalCallId }
  }, [api, startOutgoingCall])

  /**
   * 保留中の通話を内線中のオペレータに転送
   */
  const transferToOperator = useCallback(async (holdQueueName: string, targetOperatorId: string) => {
    try {
      logger.debug('[transferToOperator] 転送リクエスト:', { holdQueueName, targetOperatorId })

      const response = await api.post('/api/calls/transfer-to-operator', {
        holdQueueName,
        targetOperatorId
      })

      logger.info('[transferToOperator] 転送成功:', response)

      // 保留キュー名をクリア
      useCallStore.getState().clearHoldQueueName()

      // 保留一覧から削除
      useCallStore.getState().removeHeldCall(holdQueueName)

      return response

    } catch (error) {
      logger.error('[transferToOperator] 転送エラー:', error)

      const apiError = error as ApiError

      // 409エラー（排他制御失敗）の場合
      if (apiError.response?.status === 409) {
        useCallStore.getState().removeHeldCall(holdQueueName)
        useCallStore.getState().clearHoldQueueName()
        throw new Error(apiError.response.data.error || '既に他のオペレータが保留を解除しています')
      }

      throw error
    }
  }, [api])

  /**
   * 転送割り当てされた保留通話に接続する（ユーザークリックで呼び出す）
   * WebSocketで受信した pendingTransfer の情報を使って device.connect() を発行する。
   */
  const connectPendingTransfer = useCallback(async () => {
    const store = useCallStore.getState()
    const device = store.twilioDevice
    const transfer = store.pendingTransfer

    if (!device) {
      throw new Error('Twilio Deviceが初期化されていません')
    }
    if (!transfer) {
      throw new Error('転送情報がありません')
    }

    const { holdQueueName, originalCallId, callerNumber, callHistoryId } = transfer
    logger.info('[connectPendingTransfer] 転送通話に接続:', { holdQueueName, originalCallId, callerNumber })

    try {
      // callHistoryIdを保存
      if (callHistoryId) {
        store.setCurrentCallHistoryId(callHistoryId)
      }

      // callerNumberを設定（転送通話の発信元表示用）
      if (callerNumber) {
        store.setCallerNumber(callerNumber)
      }

      // pendingTransferをクリア（再実行防止）
      store.clearPendingTransfer()

      setCallStatus('connecting')

      // device.connect() で保留キューに接続
      const call = await device.connect({
        params: {
          queueName: holdQueueName,
          originalCallId,
          callerNumber,
        }
      })

      // 通話状態を設定
      startOutgoingCall(call, originalCallId)

      // 通話イベントリスナーを設定
      if (setupCallListenersRef.current) {
        setupCallListenersRef.current(call, originalCallId)
      }

      logger.info(`[connectPendingTransfer] device.connect() 完了: callId=${originalCallId}`)
    } catch (error) {
      logger.error('[connectPendingTransfer] 転送通話への接続エラー:', error)
      useCallStore.getState().clearPendingTransfer()
      setCallStatus('idle')
      throw error
    }
  }, [setCallStatus, startOutgoingCall])

  // グローバルストアから現在の状態を取得するヘルパーを返す
  return {
    reinitialize: initializeDevice,
    holdCall,
    resumeHeldCall,
    fetchHeldCalls,
    answerQueueCall,
    makeOutboundCall,
    makeInternalCall,
    transferToOperator,
    connectPendingTransfer,
  }
}
